import {
  GeckoMeasurement,
  GeckoMeasurementJson,
  GeckoMeasurementRange,
  GeckoMeasurementRangeSerializable,
} from 'types'

export type GeckoSummaryStatisticsHistogram = readonly [number, number][]

/**
 *  Statistics about data in the given measurement units, include
 *  - maximum reading, also indicates the ceiling of the last bin
 *  - minimum reading, also indicates the floor of the first bin
 *  - mean reading
 *  - standard deviation of distribution
 *  - histogram bins where each bin is formatted as [bin index, number of readings];
 *    if finite and defined values are uniform, then histogram is undefined
 */
export type QuantitativeSummaryStatistics = {
  mean: GeckoMeasurement
  std_dev: GeckoMeasurement
  value_range: GeckoMeasurementRange
  histogram: GeckoSummaryStatisticsHistogram | undefined
}
/**
 * A version of the QuantitativeSummaryStatistics that is serializable, useful for passing to/from web-workers
 */
export type RawQuantitativeSummaryStatistics = {
  mean: GeckoMeasurementJson
  std_dev: GeckoMeasurementJson
  value_range: GeckoMeasurementRangeSerializable
  histogram: GeckoSummaryStatisticsHistogram | undefined
}

/**
 * Serializes the QuantitativeSummaryStatistics into a RawQuantitativeSummaryStatistics so that it can be passed to and from web-workers.
 */
export function serializeQuantitativeSummaryStatistics(
  data: QuantitativeSummaryStatistics
): RawQuantitativeSummaryStatistics {
  return {
    mean: data.mean.toJSON(),
    std_dev: data.std_dev.toJSON(),
    value_range: GeckoMeasurementRange.Serialize(data.value_range),
    histogram: data.histogram,
  }
}

/**
 * Deserializes the RawQuantitativeSummaryStatistics into a QuantitativeSummaryStatistics, useful
 * for getting the full data-structure after it has been passed to/from a web-worker.
 */
export function deserializeQuantitativeSummaryStatistics(
  data: RawQuantitativeSummaryStatistics
): QuantitativeSummaryStatistics {
  return {
    mean: GeckoMeasurement.CreateFromJSON(data.mean),
    std_dev: GeckoMeasurement.CreateFromJSON(data.std_dev),
    value_range: GeckoMeasurementRange.CreateFromSerializable(data.value_range),
    histogram: data.histogram,
  }
}
